import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { linko } from "@shared/linko";
import { useQuery } from "@tanstack/react-query";
import type { FC } from "react";
import {
  buildStatisticsQueryFilters,
  type KpiAvgTimeDistributionBucket,
  type StatisticsFilters,
} from "../types";
import { KpiDialogScaffold } from "./KpiDialogScaffold";
import { KpiTimeDistributionChart } from "./KpiTimeDistributionChart";

export interface MatchEfficiencyStats {
  matchRatePercent: number | null;
  matchedMissionsCount: number;
  matchableMissionsCount: number;
  matchedWithin24hPercent: number | null;
  medianMatchTimeHours: number | null;
  avgTimeToFullHours: number | null;
}

interface MatchEfficiencyDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string | undefined;
  filters: StatisticsFilters;
  stats: MatchEfficiencyStats | undefined;
}

const formatHours = (hours: number | null | undefined) => {
  if (hours == null || hours <= 0) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} דק׳`;
  if (hours < 24) return `${hours.toFixed(1)} שע׳`;
  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours % 24);
  return remainingHours > 0 ? `${days} ימים ${remainingHours} שע׳` : `${days} ימים`;
};

const formatPercent = (value: number | null | undefined) => {
  if (value == null) return "—";
  return `${value}%`;
};

const StatTile: FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <Paper
    variant="outlined"
    sx={{
      flex: "1 1 200px",
      p: 2,
      borderRadius: 2,
      textAlign: "center",
    }}
  >
    <Typography variant="h5" fontWeight={700} color="#05204c">
      {value}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
      {label}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Paper>
);

export const MatchEfficiencyDialog: FC<MatchEfficiencyDialogProps> = ({
  open,
  onClose,
  organizationId,
  filters,
  stats,
}) => {
  const {
    data: distribution,
    isLoading: isDistributionLoading,
    error,
  } = useQuery({
    queryKey: ["statistics-kpi-avg-time-distribution", organizationId, filters],
    queryFn: async () => {
      const { data: payload } =
        await linko.statistics.queries.getKpiAvgTimeDistribution(
          buildStatisticsQueryFilters(organizationId!, filters),
        );
      return payload as KpiAvgTimeDistributionBucket[];
    },
    enabled: open && !!organizationId,
    staleTime: 60_000,
  });

  const matchHint =
    stats?.matchableMissionsCount != null
      ? `${stats.matchedMissionsCount ?? 0} מתוך ${stats.matchableMissionsCount} משימות פעילות`
      : undefined;

  return (
    <KpiDialogScaffold
      open={open}
      onClose={onClose}
      title="יעילות מאצ׳"
      explanation="מדד יעילות המאצ׳ים: כמה משימות פתוחות או מלאות מצאו לפחות מתנדב אחד, כמה מהר זה קורה בממוצע ובחציון, וכמה מהמאצ׳ים מתרחשים תוך 24 שעות מרגע שהמשימה זמינה (אישור בקשה או יצירה ישירה)."
      isLoading={false}
      error={error}
      footerNote="משימות ללא מתנדבים נספרות בשיעור המאצ׳ אך לא בחישובי הזמן."
    >
      {stats && (
        <Stack gap={3}>
          <Stack direction="row" flexWrap="wrap" gap={2}>
            <StatTile
              label="שיעור מאצ׳"
              value={formatPercent(stats.matchRatePercent)}
              hint={matchHint}
            />
            <StatTile
              label="מאצ׳ים תוך 24 שעות"
              value={formatPercent(stats.matchedWithin24hPercent)}
              hint="מתוך משימות שמצאו מתנדב"
            />
            <StatTile
              label="זמן מאצ׳ ממוצע"
              value={formatHours(stats.avgTimeToFullHours)}
            />
            <StatTile
              label="זמן מאצ׳ חציוני"
              value={formatHours(stats.medianMatchTimeHours)}
            />
          </Stack>

          {isDistributionLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!isDistributionLoading && distribution && distribution.length > 0 && (
            <Box sx={{ width: "100%" }}>
              <KpiTimeDistributionChart
                title="התפלגות זמן מאצ׳ (עד מתנדב ראשון)"
                data={distribution}
              />
            </Box>
          )}
        </Stack>
      )}
    </KpiDialogScaffold>
  );
};
